import { Button } from "@gigpro/ui/Button";
import { useInviteProToJob, useJobsWithOfferStatus } from "api/queries/hire";
import { useProProfile } from "api/queries/pros";
import { InviteToJobDialog } from "domains/hire/components/InviteToJobDialog";
import { useHasJobsAccess } from "domains/hire/hooks/useHasJobsAccess";
import React from "react";
import { captureException } from "utils/errorReporting";
import { toastApiError } from "utils/toastApiError";

interface InviteToJobButtonProps {
  proId: number;
}

/** Button + dialog for inviting a pro to a job. Only renders if feature flag enabled and pro is open to jobs. */
export const InviteToJobButton: React.FC<InviteToJobButtonProps> = ({ proId }) => {
  const [dialogOpen, setDialogOpen] = React.useState(false);

  const { data } = useProProfile(proId);
  const hasJobsEnabled = useHasJobsAccess();
  const { jobs: jobsWithOfferStatus, isLoading } = useJobsWithOfferStatus(proId, { enabled: dialogOpen });
  const inviteMutation = useInviteProToJob();

  const contractorTypes = data?.pro_preferences?.pro_contractor_type ?? [];
  const isOpenToJobs = contractorTypes.some(
    (type) => type.name.includes("Full-time") || type.name.includes("Part-time"),
  );
  const disabled = data?.is_blocked ?? false;
  if (!hasJobsEnabled || !isOpenToJobs) return null;

  const handleConfirm = async (jobIds: Array<string>) => {
    try {
      await inviteMutation.mutateAsync({ jobIds, proId });
    } catch (error) {
      toastApiError(error);
      captureException(error);
    }
  };

  return (
    <>
      <Button onClick={() => setDialogOpen(true)} variant="outlined" disabled={disabled}>
        Invite to job
      </Button>
      <InviteToJobDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        jobs={jobsWithOfferStatus}
        isLoading={isLoading}
        isSubmitting={inviteMutation.isPending}
        onConfirm={handleConfirm}
      />
    </>
  );
};
